import type { GroupRoundEnriched, Member } from '@/lib/db/types';
import {
  canDeclareBlindOnHole,
  mergeWolfConfig,
  nonWolvesInTeeOrder,
  resolveWolfHoleMode,
  wolfPickStep,
  wolfPlayerIndex,
} from '@/lib/games/formats/wolf';
import type { WolfHoleState, WolfRoundConfig } from '@/lib/games/engine/types';

interface Props {
  round: GroupRoundEnriched;
  member: Member;
  holeIx: number;
  formatId: string;
  holeState: Record<number, WolfHoleState>;
  wolfConfig: WolfRoundConfig;
  onUpdate: (nextHoleState: Record<number, WolfHoleState>) => void;
}

export function WolfHolePanel({
  round,
  member,
  holeIx,
  formatId,
  holeState,
  wolfConfig,
  onUpdate,
}: Props) {
  const players = round.players || [];
  if (players.length < 4) return null;

  const cfg = mergeWolfConfig(wolfConfig);
  const holeNum = holeIx + 1;
  const hs = holeState[holeNum] ?? {};
  const wolfK = wolfPlayerIndex(holeIx);
  const wolfPlayer = players[wolfK];
  const pickOrder = nonWolvesInTeeOrder(holeIx);
  const isOrganizer = round.organizer_id === member.id;
  const isWolf = wolfPlayer?.member_id === member.id;
  const canControl = isWolf || isOrganizer;

  const { mode, partnerIndex, locked } = resolveWolfHoleMode(hs, holeIx, cfg);
  const step = wolfPickStep(hs, holeIx);
  const showBlind = canDeclareBlindOnHole(cfg, hs, holeIx);

  const patchHole = (patch: Partial<WolfHoleState>) => {
    const next: WolfHoleState = { ...hs, ...patch, locked: patch.locked ?? hs.locked };
    onUpdate({ ...holeState, [holeNum]: next });
  };

  const modeBadge = () => {
    if (mode === 'blind') return '🐺 Blind Wolf';
    if (mode === 'lone') return '🐺 Lone Wolf';
    if (mode === 'partner' && partnerIndex != null) {
      return `🤝 ${players[partnerIndex]?.display_name ?? 'Partner'}`;
    }
    return null;
  };

  const badge = modeBadge();

  return (
    <div className="rounded-xl border border-line bg-paper p-3">
      <div className="mb-2 text-xs font-bold text-navy">Wolf</div>
      <div className="mb-1 text-[11px] text-ink-soft">
        🐺 {wolfPlayer?.display_name} · tees first (hole {holeNum})
      </div>
      {badge && (
        <div className="mb-2 inline-block rounded-full bg-navy/10 px-2.5 py-0.5 text-[10px] font-bold text-navy">
          {badge}
          {locked && ' · locked'}
        </div>
      )}

      {!canControl && (
        <p className="text-[10px] text-mute">Wolf ({wolfPlayer?.display_name}) or organizer sets decisions.</p>
      )}

      {canControl && !locked && mode == null && (
        <div className="space-y-2">
          {showBlind && (
            <button
              type="button"
              onClick={() => patchHole({
                blindDeclared: true,
                mode: 'blind',
                partnerIndex: cfg.blindPartnerRule === 'first-tee' ? pickOrder[0] : null,
                locked: true,
                passes: [],
              })}
              className="w-full rounded-lg border-2 border-gold bg-gold/10 py-2 text-xs font-bold text-navy"
            >
              🐺 Blind Wolf (before tee shots)
            </button>
          )}

          {step >= 0 && (
            <div className="rounded-lg bg-cream-warm p-2">
              <div className="mb-1.5 text-[10px] font-bold uppercase text-mute">
                After {players[pickOrder[step]]?.display_name}&apos;s tee shot
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => patchHole({
                    mode: 'partner',
                    partnerIndex: pickOrder[step],
                    locked: true,
                  })}
                  className="rounded-full bg-navy px-3 py-1.5 text-[11px] font-bold text-cream"
                >
                  🤝 Pick {players[pickOrder[step]]?.display_name}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const passes = [...(hs.passes ?? []), pickOrder[step]];
                    if (passes.length >= pickOrder.length) {
                      patchHole({ mode: 'lone', passes, locked: true, partnerIndex: null });
                    } else {
                      patchHole({ passes });
                    }
                  }}
                  className="rounded-full border border-line px-3 py-1.5 text-[11px] font-bold text-ink-soft"
                >
                  ➡️ Pass
                </button>
                {step < pickOrder.length - 1 && (
                  <button
                    type="button"
                    onClick={() => patchHole({ mode: 'lone', locked: true, partnerIndex: null })}
                    className="rounded-full border border-gold px-3 py-1.5 text-[11px] font-bold text-gold-deep"
                  >
                    🐺 Go Lone Wolf
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {canControl && mode === 'blind' && cfg.blindPartnerRule === 'organizer' && partnerIndex == null && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="w-full text-[10px] font-bold text-mute">Pick Blind partner:</span>
          {pickOrder.map((ix) => (
            <button
              key={ix}
              type="button"
              onClick={() => patchHole({ partnerIndex: ix, locked: true })}
              className="rounded-full border border-line px-2.5 py-1 text-[11px] font-bold"
            >
              {players[ix]?.display_name}
            </button>
          ))}
        </div>
      )}

      {formatId === 'lone-wolf' && canControl && !locked && mode == null && step < 0 && (
        <button
          type="button"
          onClick={() => patchHole({ mode: 'lone', locked: true })}
          className="mt-2 rounded-full border border-gold px-3 py-1.5 text-[11px] font-bold text-gold-deep"
        >
          🐺 Lone Wolf only
        </button>
      )}
    </div>
  );
}
